import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useUserRoles } from '../../contexts/UserRolesContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { getTeamMembersByManagerId, getTeamMemberWithDetails, updateTeamMemberPermissions } from '../../services/teamMemberService';
import type { User, Role, UserPermission } from '../../types';
import { Users, Building2, Mail, Phone, Check, X, ChevronRight, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import Button from '../../components/ui/Button';
import { SectionSearchBar, SectionHeader } from '../../components/ui';
import FeatureAccessPlaceholder from '../../components/subscription/FeatureAccessPlaceholder';

interface TeamMemberWithDetails extends User {
  roles: Role[];
  permissions: UserPermission | null;
  officeName?: string;
}

export default function TeamMembers() {
  const { user } = useAuth();
  const { strings, language, isRTL } = useLocalization();
  const { offices, loading: officesLoading } = useUserRoles();
  const { isFeatureAvailable } = useSubscription();
  
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>('');
  const [allTeamMembers, setAllTeamMembers] = useState<User[]>([]); // Cache all team members
  const [teamMembers, setTeamMembers] = useState<TeamMemberWithDetails[]>([]);
  const [selectedMember, setSelectedMember] = useState<TeamMemberWithDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<UserPermission | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter team members based on search query
  const filteredTeamMembers = teamMembers.filter(member => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      member.firstName?.toLowerCase().includes(searchLower) ||
      member.lastName?.toLowerCase().includes(searchLower) ||
      member.email?.toLowerCase().includes(searchLower) ||
      member.phoneNumber?.toLowerCase().includes(searchLower) ||
      member.phoneNumber2?.toLowerCase().includes(searchLower) ||
      member.id?.toLowerCase().includes(searchLower)
    );
  });

  // Load all team members once when component mounts or user changes
  const loadAllTeamMembers = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Loading all team members for manager:', user.id);
      
      // Fetch all team members once for the current user
      const fetchedTeamMembers = await getTeamMembersByManagerId(user.id);
      console.log('📊 All team members fetched:', fetchedTeamMembers.length);
      
      setAllTeamMembers(fetchedTeamMembers);
    } catch (err) {
      console.error('❌ Error loading all team members:', err);
      setError('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Filter team members by selected office (local filtering for performance)
  const filterTeamMembersByOffice = useCallback(async () => {
    if (!selectedOfficeId || allTeamMembers.length === 0) {
      setTeamMembers([]);
      return;
    }

    try {
      console.log('🔍 Filtering team members for office:', selectedOfficeId);
      
      // Filter by selected office locally for better performance
      const teamMembersForOffice = allTeamMembers.filter(member => 
        member.officesIDsList && member.officesIDsList.includes(selectedOfficeId)
      );
      console.log('📊 Team members for selected office:', teamMembersForOffice.length);
      
      // Get detailed information for each team member (roles and permissions)
      const membersWithDetails: TeamMemberWithDetails[] = await Promise.all(
        teamMembersForOffice.map(async (member) => {
          const details = await getTeamMemberWithDetails(member.id, selectedOfficeId);
          const office = offices.find(o => o.id === selectedOfficeId);
          
          return {
            ...member,
            roles: details?.roles || [],
            permissions: details?.permissions || null,
            officeName: office?.name || ''
          };
        })
      );
      
      console.log('✅ Final team members with details:', membersWithDetails.length);
      setTeamMembers(membersWithDetails);
    } catch (err) {
      console.error('❌ Error filtering team members:', err);
      setError('Failed to filter team members');
    }
  }, [selectedOfficeId, allTeamMembers, offices]);

  // Load all team members when component mounts or user changes
  useEffect(() => {
    if (user?.id) {
      loadAllTeamMembers();
    }
  }, [user?.id, loadAllTeamMembers]);

  // Set default office when offices are loaded
  useEffect(() => {
    if (offices.length > 0 && !selectedOfficeId) {
      setSelectedOfficeId(offices[0].id);
    }
  }, [offices, selectedOfficeId]);

  // Filter team members when office is selected or all team members are loaded
  useEffect(() => {
    if (selectedOfficeId && allTeamMembers.length > 0) {
      filterTeamMembersByOffice();
    }
  }, [selectedOfficeId, allTeamMembers, filterTeamMembersByOffice]);

  const handleOfficeChange = (officeId: string) => {
    setSelectedOfficeId(officeId);
    setSelectedMember(null); // Clear selected member when changing office
    setEditingMember(null);
    setPermissions(null);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleRefresh = () => {
    loadAllTeamMembers();
  };

  const exportToExcel = () => {
    try {
      const exportData = filteredTeamMembers.map(member => ({
        'Member ID': member.id,
        'First Name': member.firstName || 'N/A',
        'Last Name': member.lastName || 'N/A',
        'Email': member.email || 'N/A',
        'Phone Number': member.phoneNumber || 'N/A',
        'Secondary Phone': member.phoneNumber2 || 'N/A',
        'Office Name': member.officeName || 'N/A',
        'Roles Count': member.roles ? member.roles.length : 0,
        'Roles': member.roles ? member.roles.map(role => role.officeName).join(', ') : 'N/A',
        'Permissions': member.permissions ? 'Yes' : 'No',
        'Created At': member.createdAt ? new Date(parseInt(member.createdAt)).toLocaleDateString() : 'N/A',
        'Updated At': member.updatedAt ? new Date(parseInt(member.updatedAt)).toLocaleDateString() : 'N/A',
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, 'TeamMembers');
      XLSX.writeFile(wb, `team_members_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error exporting team members to Excel:', err);
      setError(language === 'ar' ? 'فشل في تصدير أعضاء الفريق' : 'Failed to export team members to Excel.');
    }
  };

  const handleAddMember = () => {
    // TODO: Add team member functionality
    console.log('Add team member clicked');
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleMemberSelect = (member: TeamMemberWithDetails) => {
    setSelectedMember(member);
    setEditingMember(null);
    setPermissions(null);
  };

  const handleEditPermissions = (member: TeamMemberWithDetails) => {
    setEditingMember(member.id);
    setPermissions(member.permissions ? { ...member.permissions } : getDefaultPermissions());
  };

  const getDefaultPermissions = (): UserPermission => ({
    id: '',
    showClientsList: false,
    updateClientProfile: false,
    removeClientFromOffice: false,
    showOfficeList: false,
    updateOfficeProfile: false,
    removeOfficeFromOffice: false,
    showEvents: false,
    showEventsList: false,
    updateEvent: false,
    removeEvent: false,
    showReports: false,
    showReportsList: false,
    updateReport: false,
    removeReport: false,
    showClientFilesList: false,
    updateClientFile: false,
    removeClientFile: false,
    showSessionsList: false,
    updateSession: false,
    deleteSession: false,
    showLawyersList: false,
    updateLawyerProfile: false,
    removeLawyerFromOffice: false
  });

  const handlePermissionChange = (permission: keyof UserPermission, value: boolean) => {
    if (permissions) {
      setPermissions({
        ...permissions,
        [permission]: value
      });
    }
  };

  const handleSavePermissions = async () => {
    if (!editingMember || !permissions || !selectedOfficeId) return;

    try {
      setLoading(true);
      await updateTeamMemberPermissions(editingMember, selectedOfficeId, permissions);
      setLoading(false);
      
      // Reload filtered data (local operation, no loading needed)
      await filterTeamMembersByOffice();
      setEditingMember(null);
      setPermissions(null);
    } catch (err) {
      console.error('Error updating permissions:', err);
      setError('Failed to update permissions');
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
    setPermissions(null);
  };

  if (officesLoading || (loading && !teamMembers.length)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{strings.common.loading}</p>
        </div>
      </div>
    );
  }

  // Show message if user has no offices
  if (!officesLoading && offices.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {strings.teamMembers.noOfficeSelected}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            You don't have access to any offices yet.
          </p>
        </div>
      </div>
    );
  }

  // Check if user has access to team management
  const hasTeamManagement = isFeatureAvailable('teamManagement');

  // Show placeholder if user doesn't have access
  if (!hasTeamManagement) {
    return (
      <FeatureAccessPlaceholder
        featureName="Team Management"
        featureNameAr="إدارة الفريق"
        description="Manage your team members, assign roles, and control permissions across your offices."
        descriptionAr="إدارة أعضاء فريقك، تعيين الأدوار، والتحكم في الصلاحيات عبر مكاتبك."
        icon={<Users className="w-12 h-12 text-white" />}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <SectionHeader
          title={strings.teamMembers.title}
          description={strings.teamMembers.teamMembersList}
          language={language}
          isRTL={isRTL}
          actionButtons={[
            {
              label: language === 'ar' ? 'تصدير إكسل' : 'Export Excel',
              onClick: exportToExcel,
              icon: <Download className="w-4 h-4" />,
              variant: 'secondary',
              requiresProfessional: false
            }
          ]}
          refreshButton={{
            onClick: handleRefresh,
            disabled: !selectedOfficeId,
            loading: loading
          }}
          addButton={{
            label: language === 'ar' ? 'إضافة عضو فريق' : 'Add Team Member',
            onClick: handleAddMember
          }}
        >
          {/* Office Selection */}
          {offices.length > 0 && (
            <div className="mb-6">
              <label className={`block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                {strings.teamMembers.selectOffice}
              </label>
              <div className={`flex flex-wrap gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {offices.map((office) => (
                  <button
                    key={office.id}
                    onClick={() => handleOfficeChange(office.id)}
                    className={`px-4 py-3 rounded-xl border-2 transition-all duration-300 flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} ${
                      selectedOfficeId === office.id
                        ? 'border-primary bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 shadow-lg'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md'
                    }`}
                  >
                    <Building2 className="w-5 h-5" />
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <div className="font-semibold">{office.name}</div>
                      <div className="text-sm opacity-75">{office.phoneNumber || 'No phone'}</div>
                    </div>
                    {selectedOfficeId === office.id && (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6">
              <div className="p-6 border border-red-200 bg-red-50 dark:bg-red-900/20 rounded-3xl">
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            </div>
          )}
        </SectionHeader>

        {/* Main Content - Two Panel Layout */}
        {selectedOfficeId ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel - Team Members List */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                  <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                    {strings.teamMembers.teamMembersList}
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    {searchQuery ? (
                      `${filteredTeamMembers.length} of ${teamMembers.length} team member${teamMembers.length !== 1 ? 's' : ''} found`
                    ) : (
                      `${teamMembers.length} team member${teamMembers.length !== 1 ? 's' : ''} found`
                    )}
                  </p>
                  
                  {/* Search Bar */}
                  <div className="mt-4">
                    <SectionSearchBar
                      searchQuery={searchQuery}
                      onSearchChange={handleSearchChange}
                      onClearSearch={handleClearSearch}
                      placeholder="Search team members..."
                      language="en"
                    />
                  </div>
                </div>
                
                <div className="p-4">
                  {filteredTeamMembers.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Users className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">
                        {searchQuery ? 'No team members found matching your search' : 'No team members found for this office'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredTeamMembers.map((member) => {
                        const isSelected = selectedMember?.id === member.id;
                        
                        return (
                          <div
                            key={member.id}
                            className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                              isSelected 
                                ? 'bg-blue-50 dark:bg-blue-900/20 border-primary shadow-sm' 
                                : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                            }`}
                            onClick={() => handleMemberSelect(member)}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                                  <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-slate-800 dark:text-white text-sm truncate">
                                  {member.firstName} {member.lastName}
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  {member.phoneNumber || 'No phone number'}
                                </p>
                              </div>
                              {isSelected && (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel - Selected Member Details */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                  <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                    {strings.teamMembers.memberDetails}
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    {selectedMember ? 'View member information and permissions' : 'Select a team member to view details'}
                  </p>
                </div>
                
                <div className="p-4">
                  {!selectedMember ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Users className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">
                        Select a team member to view details
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Member Basic Info */}
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="h-16 w-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                            {selectedMember.firstName} {selectedMember.lastName}
                          </h3>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-slate-600 dark:text-slate-400">
                            <div className="flex items-center space-x-1">
                              <Mail className="h-4 w-4" />
                              <span>{selectedMember.email}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Phone className="h-4 w-4" />
                              <span>{selectedMember.phoneNumber}</span>
                            </div>
                          </div>
                          {selectedMember.officeName && (
                            <div className="flex items-center space-x-1 mt-2 text-sm text-slate-600 dark:text-slate-400">
                              <Building2 className="h-4 w-4" />
                              <span>{selectedMember.officeName}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="secondary"
                            onClick={() => handleEditPermissions(selectedMember)}
                            label={strings.teamMembers.editMember}
                          />
                        </div>
                      </div>

                      {/* Permissions Section */}
                      {selectedMember.permissions && (
                        <div className="pt-6 border-t border-slate-200 dark:border-slate-600">
                          <h4 className="text-sm font-medium text-slate-800 dark:text-white mb-4">
                            {strings.teamMembers.permissions}
                          </h4>
                          <div className="grid grid-cols-1 gap-4">
                            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-600">
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {strings.teamMembers.accessClients}
                              </span>
                              {selectedMember.permissions.showClientsList ? (
                                <Check className="h-5 w-5 text-green-500" />
                              ) : (
                                <X className="h-5 w-5 text-red-500" />
                              )}
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-600">
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {strings.teamMembers.accessLegalFiles}
                              </span>
                              {selectedMember.permissions.showClientFilesList ? (
                                <Check className="h-5 w-5 text-green-500" />
                              ) : (
                                <X className="h-5 w-5 text-red-500" />
                              )}
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-600">
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {strings.teamMembers.accessEvents}
                              </span>
                              {selectedMember.permissions.showEvents ? (
                                <Check className="h-5 w-5 text-green-500" />
                              ) : (
                                <X className="h-5 w-5 text-red-500" />
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                {strings.teamMembers.teamMembersList}
              </h2>
            </div>
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-800 dark:text-white mb-2">
                {strings.teamMembers.noOfficeSelected}
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Please select an office to view team members
              </p>
            </div>
          </div>
        )}

        {/* Edit Permissions Modal */}
        {editingMember && permissions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-800 dark:text-white">
                    {strings.teamMembers.updatePermissions}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    Configure team member access permissions
                  </p>
                </div>
                
                <div className="p-6">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={permissions.showClientsList}
                          onChange={(e) => handlePermissionChange('showClientsList', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-slate-100 border-slate-300 rounded focus:ring-primary dark:focus:ring-blue-600 dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600"
                        />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {strings.teamMembers.accessClients}
                        </span>
                      </label>
                      
                      <label className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={permissions.showClientFilesList}
                          onChange={(e) => handlePermissionChange('showClientFilesList', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-slate-100 border-slate-300 rounded focus:ring-primary dark:focus:ring-blue-600 dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600"
                        />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {strings.teamMembers.accessLegalFiles}
                        </span>
                      </label>
                      
                      <label className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={permissions.showEvents}
                          onChange={(e) => handlePermissionChange('showEvents', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-slate-100 border-slate-300 rounded focus:ring-primary dark:focus:ring-blue-600 dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600"
                        />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {strings.teamMembers.accessEvents}
                        </span>
                      </label>
                      
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                    <Button
                      variant="secondary"
                      onClick={handleCancelEdit}
                      disabled={loading}
                      label={strings.common.cancel}
                    />
                    <Button
                      onClick={handleSavePermissions}
                      disabled={loading}
                      label={loading ? strings.common.loading : strings.common.save}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}